/**
 * ROIContract — Phase 2 Tactical Win #2.
 *
 * Public page at /pricing/contract that converts the marketing claim
 * ("$0 upfront · 25% of verified savings · 3× ROI guarantee") into a
 * real signable PDF a CFO can sign.
 *
 * Flow:
 *   1. Show contract highlights (loaded from GET /api/roi-contract/preview)
 *   2. Inline form: company name, signer name, signer email, optional
 *      baseline spend + estimated savings (for illustrative projection)
 *   3. CTA → POST /api/roi-contract/generate → download PDF
 *
 * No login required for the preview; download triggers demo-login if
 * the visitor isn't authenticated so the marketing → product handoff
 * stays frictionless.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, FileDown, CheckCircle2, ShieldCheck, DollarSign,
  Calculator, Lock,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ROIContract() {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    customer_company: '',
    customer_signer_name: '',
    customer_signer_email: '',
    customer_signer_title: 'CFO',
    estimated_monthly_cloud_spend_usd: '',
    estimated_monthly_savings_usd: '',
  });

  useEffect(() => {
    axios.get(`${API}/roi-contract/preview`)
      .then((r) => setPreview(r.data))
      .catch(() => {});
  }, []);

  const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const ensureToken = async () => {
    let tok = localStorage.getItem('token');
    if (!tok) {
      const r = await axios.post(`${API}/auth/demo-login`, {});
      tok = r.data.access_token || r.data.token;
      localStorage.setItem('token', tok);
    }
    return tok;
  };

  const validate = () => {
    if (!form.customer_company.trim()) return 'Company name is required';
    if (!form.customer_signer_name.trim()) return 'Signer name is required';
    if (!form.customer_signer_email.includes('@')) return 'Valid signer email is required';
    return null;
  };

  const generate = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setBusy(true);
    try {
      const tok = await ensureToken();
      const r = await axios.post(`${API}/roi-contract/generate`, {
        customer_company:      form.customer_company.trim(),
        customer_signer_name:  form.customer_signer_name.trim(),
        customer_signer_email: form.customer_signer_email.trim(),
        customer_signer_title: form.customer_signer_title.trim() || 'CFO',
        estimated_monthly_cloud_spend_usd: parseFloat(form.estimated_monthly_cloud_spend_usd) || 0,
        estimated_monthly_savings_usd:     parseFloat(form.estimated_monthly_savings_usd) || 0,
      }, {
        headers: { Authorization: `Bearer ${tok}` },
        responseType: 'blob',
      });

      // Trigger file download
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `CARTIE-ROI-Contract-${form.customer_company.replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contract downloaded — review, sign, and email back to founder@cartieai.com');
    } catch (e) {
      toast.error('Could not generate contract', { description: e?.message });
    } finally {
      setBusy(false);
    }
  };

  const sharePct = 25;
  const projSavings = parseFloat(form.estimated_monthly_savings_usd) || 0;
  const projFee = projSavings * sharePct / 100;
  const projNet = projSavings - projFee;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Helmet>
        <title>Sign the ROI agreement · CARTIEAI</title>
      </Helmet>

      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* ── Top bar ── */}
        <div className="mb-6">
          <Link
            to="/pricing"
            className="inline-flex items-center text-sm text-slate-400 hover:text-slate-100"
            data-testid="roic-back-link"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to pricing
          </Link>
        </div>

        {/* ── Hero ── */}
        <div className="mb-10" data-testid="roic-hero">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight">
            <span className="text-slate-100">Sign the </span>
            <span className="text-emerald-300">ROI agreement</span>
          </h1>
          <p className="text-lg text-slate-400 mt-4 max-w-3xl">
            Generate, download and countersign a real Performance ROI Agreement
            in 60 seconds. <span className="text-slate-200 font-medium">No legal review on our end</span> —
            we built it Stripe-style: fair, exit-anytime, no minimum commitment.
          </p>
        </div>

        {/* ── Headline highlights ── */}
        {preview && (
          <div className="grid sm:grid-cols-3 gap-3 mb-8" data-testid="roic-highlights">
            {preview.highlights.map((h, i) => (
              <div key={i}
                   className="flex items-start gap-2 p-3 rounded-lg bg-emerald-500/5 ring-1 ring-emerald-500/20 text-sm"
                   data-testid={`roic-highlight-${i}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
                <span className="text-slate-200">{h}</span>
              </div>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-5 gap-6">
          {/* ── Form ── */}
          <Card className="lg:col-span-3 bg-slate-900/40 ring-1 ring-slate-800" data-testid="roic-form-card">
            <CardContent className="p-6 space-y-5">
              <h2 className="text-base font-medium text-slate-100">Your company</h2>

              <div>
                <Label className="text-xs text-slate-400">Company name *</Label>
                <Input
                  value={form.customer_company}
                  onChange={(e) => setField('customer_company', e.target.value)}
                  placeholder="Acme Corp, Inc."
                  className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                  data-testid="roic-company-input"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-400">Signer name *</Label>
                  <Input
                    value={form.customer_signer_name}
                    onChange={(e) => setField('customer_signer_name', e.target.value)}
                    placeholder="Jane Doe"
                    className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                    data-testid="roic-signer-name"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Signer title</Label>
                  <Input
                    value={form.customer_signer_title}
                    onChange={(e) => setField('customer_signer_title', e.target.value)}
                    placeholder="CFO"
                    className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                    data-testid="roic-signer-title"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-400">Signer email *</Label>
                <Input
                  type="email"
                  value={form.customer_signer_email}
                  onChange={(e) => setField('customer_signer_email', e.target.value)}
                  placeholder="jane@acme.com"
                  className="bg-slate-950 border-slate-700 text-slate-100 mt-1"
                  data-testid="roic-signer-email"
                />
              </div>

              <div className="pt-4 border-t border-slate-800">
                <h2 className="text-base font-medium text-slate-100 mb-1">Optional projection</h2>
                <p className="text-xs text-slate-500 mb-4">
                  Fill these in to print an illustrative ROI estimate inside the PDF. Leave blank for a clean framework agreement.
                </p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-400">Current monthly cloud spend (USD)</Label>
                    <Input
                      type="number"
                      value={form.estimated_monthly_cloud_spend_usd}
                      onChange={(e) => setField('estimated_monthly_cloud_spend_usd', e.target.value)}
                      placeholder="50000"
                      className="bg-slate-950 border-slate-700 text-slate-100 mt-1 font-mono"
                      data-testid="roic-baseline-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Expected monthly savings (USD)</Label>
                    <Input
                      type="number"
                      value={form.estimated_monthly_savings_usd}
                      onChange={(e) => setField('estimated_monthly_savings_usd', e.target.value)}
                      placeholder="15000"
                      className="bg-slate-950 border-slate-700 text-slate-100 mt-1 font-mono"
                      data-testid="roic-savings-input"
                    />
                  </div>
                </div>
              </div>

              <Button
                onClick={generate}
                disabled={busy}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-medium"
                size="lg"
                data-testid="roic-generate-btn"
              >
                <FileDown className="w-4 h-4 mr-2" />
                {busy ? 'Generating PDF…' : 'Generate & download the PDF'}
              </Button>

              <p className="text-[11px] text-slate-500">
                <Lock className="w-3 h-3 inline mr-1" />
                Generation is audit-logged. We never share your company name externally.
              </p>
            </CardContent>
          </Card>

          {/* ── Live ROI math sidebar ── */}
          <Card className="lg:col-span-2 bg-slate-900/40 ring-1 ring-emerald-500/30 h-fit"
                data-testid="roic-math-card">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 text-xs text-emerald-300 mb-3">
                <Calculator className="w-4 h-4" /> Live math
              </div>
              <div className="space-y-3 text-sm">
                <Row label="Expected savings"
                     value={projSavings ? `$${projSavings.toLocaleString()}/mo` : '—'}
                     tone="emerald" />
                <Row label={`CARTIE share (${sharePct}%)`}
                     value={projSavings ? `$${projFee.toLocaleString()}/mo` : '—'}
                     tone="slate" />
                <div className="border-t border-slate-800 pt-3">
                  <Row label="You keep"
                       value={projSavings ? `$${projNet.toLocaleString()}/mo` : '—'}
                       tone="emerald" big />
                </div>
                <Row label="Annual net to you"
                     value={projSavings ? `$${(projNet * 12).toLocaleString()}/yr` : '—'}
                     tone="emerald" />
              </div>
              <div className="mt-5 pt-4 border-t border-slate-800 space-y-2 text-xs text-slate-400">
                <p className="flex items-start gap-2"><ShieldCheck className="w-3.5 h-3.5 text-emerald-300 mt-0.5" />
                  <span><strong className="text-slate-200">3× ROI guarantee.</strong> Booked savings &lt; 3× our fee in 12 months → full refund.</span></p>
                <p className="flex items-start gap-2"><DollarSign className="w-3.5 h-3.5 text-emerald-300 mt-0.5" />
                  <span><strong className="text-slate-200">$0 upfront.</strong> No setup, no minimum commitment.</span></p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── What's inside the PDF (preview) ── */}
        {preview && (
          <Card className="bg-slate-900/40 ring-1 ring-slate-800 mt-10" data-testid="roic-toc-card">
            <CardContent className="p-6">
              <h3 className="text-sm font-medium text-slate-200 mb-3">What&apos;s inside the PDF</h3>
              <ol className="grid sm:grid-cols-2 gap-2 text-sm text-slate-300">
                {preview.sections.map((sec, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/30 text-emerald-300 text-[10px] flex items-center justify-center">
                      {i + 1}
                    </span>
                    {sec}
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, tone = 'slate', big = false }) {
  const valueClass = big
    ? `font-mono text-2xl ${tone === 'emerald' ? 'text-emerald-300' : 'text-slate-100'}`
    : `font-mono ${tone === 'emerald' ? 'text-emerald-300' : 'text-slate-200'}`;
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-400">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
